/**
 * Polyline helpers shared by mapgen (coast data) and the client (per-zoom coast LOD):
 * Ramer–Douglas–Peucker simplification, spike removal and Chaikin smoothing on flat
 * [x, y, x, y, …] arrays. A polyline whose last point equals its first is a closed ring.
 */

type Pts = ArrayLike<number>;

export function isClosedRing(p: Pts): boolean {
  const n = p.length;
  return n >= 8 && p[0] === p[n - 2] && p[1] === p[n - 1];
}

/** Diagonal of the bounding box. */
export function polylineExtent(p: Pts): number {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (let i = 0; i < p.length; i += 2) {
    if (p[i] < x0) x0 = p[i];
    if (p[i] > x1) x1 = p[i];
    if (p[i + 1] < y0) y0 = p[i + 1];
    if (p[i + 1] > y1) y1 = p[i + 1];
  }
  return Math.hypot(x1 - x0, y1 - y0);
}

/** Ramer–Douglas–Peucker (keeps the end points; a closed ring stays closed). */
export function simplifyRdp(pts: Pts, tol: number): number[] {
  const n = pts.length / 2;
  if (n <= 2 || tol <= 0) return Array.from(pts);
  const keep = new Uint8Array(n);
  keep[0] = 1;
  keep[n - 1] = 1;
  const tol2 = tol * tol;
  const stack: number[] = [0, n - 1];
  while (stack.length) {
    const b = stack.pop()!;
    const a = stack.pop()!;
    const ax = pts[a * 2];
    const ay = pts[a * 2 + 1];
    const dx = pts[b * 2] - ax;
    const dy = pts[b * 2 + 1] - ay;
    const len2 = dx * dx + dy * dy;
    let worst = -1;
    let at = -1;
    for (let i = a + 1; i < b; i++) {
      const px = pts[i * 2] - ax;
      const py = pts[i * 2 + 1] - ay;
      const t = len2 > 0 ? Math.max(0, Math.min(1, (px * dx + py * dy) / len2)) : 0;
      const d2 = (px - t * dx) ** 2 + (py - t * dy) ** 2;
      if (d2 > worst) {
        worst = d2;
        at = i;
      }
    }
    if (worst > tol2) {
      keep[at] = 1;
      stack.push(a, at, at, b);
    }
  }
  const out: number[] = [];
  for (let i = 0; i < n; i++) if (keep[i]) out.push(pts[i * 2], pts[i * 2 + 1]);
  return out;
}

/**
 * Remove needle vertices: a vertex whose two edges meet at less than `maxAngleDeg` while its
 * neighbours are closer than `maxWidth` to each other (a fjord or spit narrower than the
 * line can show), plus zero-length edges. Repeats until stable, so a whole sliver collapses.
 * End points of open polylines are kept; closed rings keep at least 3 corners.
 */
export function despike(pts: Pts, maxWidth: number, maxAngleDeg = 25): number[] {
  const n = pts.length / 2;
  const closed = isClosedRing(pts);
  const m = closed ? n - 1 : n;
  if (m < 3) return Array.from(pts);
  const prev = new Int32Array(m);
  const next = new Int32Array(m);
  const alive = new Uint8Array(m).fill(1);
  for (let i = 0; i < m; i++) {
    prev[i] = closed ? (i - 1 + m) % m : i - 1;
    next[i] = closed ? (i + 1) % m : i + 1 < m ? i + 1 : -1;
  }
  const cosMax = Math.cos((maxAngleDeg * Math.PI) / 180);
  const w2 = maxWidth * maxWidth;
  const spike = (i: number): boolean => {
    const p = prev[i];
    const q = next[i];
    if (p < 0 || q < 0) return false;
    const vx = pts[i * 2];
    const vy = pts[i * 2 + 1];
    const ax = pts[p * 2] - vx;
    const ay = pts[p * 2 + 1] - vy;
    const bx = pts[q * 2] - vx;
    const by = pts[q * 2 + 1] - vy;
    const la = Math.hypot(ax, ay);
    const lb = Math.hypot(bx, by);
    if (la < 1e-9 || lb < 1e-9) return true;
    if ((ax * bx + ay * by) / (la * lb) <= cosMax) return false;
    return (ax - bx) ** 2 + (ay - by) ** 2 < w2;
  };
  let count = m;
  const stack: number[] = [];
  for (let i = m - 1; i >= 0; i--) stack.push(i);
  while (stack.length) {
    if (closed && count <= 3) break;
    const i = stack.pop()!;
    if (!alive[i] || !spike(i)) continue;
    alive[i] = 0;
    count--;
    const p = prev[i];
    const q = next[i];
    next[p] = q;
    prev[q] = p;
    stack.push(p, q);
  }
  let start = 0;
  if (closed) while (!alive[start]) start++;
  const out: number[] = [];
  let i = start;
  do {
    out.push(pts[i * 2], pts[i * 2 + 1]);
    i = next[i];
  } while (i >= 0 && i !== start);
  if (closed) out.push(out[0], out[1]);
  return out;
}

/** Chaikin corner cutting (`iterations` rounds); open polylines keep their end points. */
export function chaikin(pts: Pts, iterations = 1): number[] {
  let cur = Array.from(pts);
  for (let it = 0; it < iterations; it++) {
    const n = cur.length / 2;
    if (n < 3) return cur;
    const closed = isClosedRing(cur);
    const out: number[] = [];
    if (!closed) out.push(cur[0], cur[1]);
    for (let i = 0; i < n - 1; i++) {
      const ax = cur[i * 2];
      const ay = cur[i * 2 + 1];
      const bx = cur[i * 2 + 2];
      const by = cur[i * 2 + 3];
      const first = !closed && i === 0;
      const last = !closed && i === n - 2;
      if (!first) out.push(0.75 * ax + 0.25 * bx, 0.75 * ay + 0.25 * by);
      if (!last) out.push(0.25 * ax + 0.75 * bx, 0.25 * ay + 0.75 * by);
    }
    if (closed) out.push(out[0], out[1]);
    else out.push(cur[(n - 1) * 2], cur[(n - 1) * 2 + 1]);
    cur = out;
  }
  return cur;
}

/**
 * Simplify for display at a given tolerance: RDP, then needle removal, keeping closed
 * rings as real rings (≥ 3 corners) — a ring that would collapse is retried finer, so
 * small islands never turn into back-and-forth slivers. Returns null if `minExtent`
 * drops a closed ring.
 */
export function simplifyForDisplay(pts: Pts, tol: number, needleWidth: number, minExtent = 0): number[] | null {
  const closed = isClosedRing(pts);
  if (closed && minExtent > 0 && polylineExtent(pts) < minExtent) return null;
  let t = tol;
  for (let attempt = 0; attempt < 6; attempt++) {
    const s = despike(simplifyRdp(pts, t), needleWidth);
    if (!closed) return s.length >= 4 ? s : null;
    if (s.length >= 8) return s;
    t *= 0.5;
  }
  return closed ? null : Array.from(pts);
}
